import { useState } from 'react';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useDashboardTheme, cardSx, panelHeaderSx, tableHeadRowSx, tableRowSx, scrollbarSx, fmt, computePnl } from '../utils';

const COLUMNS = [
  { key: 'displayName', label: 'Player' },
  { key: 'normalBidCount', label: 'Bids' },
  { key: 'normalBidTotal', label: 'Bid Total ($)' },
  { key: 'totalEarningsUsd', label: 'Earnings ($)' },
  { key: 'blockRewardUsd', label: 'Block ($)', nested: 'earningsBreakdown' },
  { key: 'bossUsd', label: 'Boss ($)', nested: 'earningsBreakdown' },
  { key: 'airdropUsd', label: 'Airdrop ($)', nested: 'earningsBreakdown' },
  { key: 'pnl', label: 'P&L ($)', computed: true },
  { key: 'airdropBidAmountUsd', label: 'Airdrop Wt ($)' },
  { key: 'autoBid', label: 'Auto-Bid' }
];

function PlayerBreakdown({ liveState, loading }) {
  const t = useDashboardTheme();
  const [expanded, setExpanded] = useState(true);
  const [orderBy, setOrderBy] = useState('normalBidTotal');
  const [order, setOrder] = useState('desc');
  const [copySnack, setCopySnack] = useState('');

  const handleCopyUid = (userId) => {
    navigator.clipboard.writeText(userId).then(() => {
      setCopySnack('Copied ' + userId.slice(0, 12) + '…');
    }).catch(() => { setCopySnack('Copy failed'); });
  };

  const players = liveState?.players || [];

  const sorted = [...players].sort((a, b) => {
    const colDef = COLUMNS.find((c) => c.key === orderBy);
    let av = colDef?.computed ? computePnl(a) : colDef?.nested ? a[colDef.nested]?.[orderBy] : a[orderBy];
    let bv = colDef?.computed ? computePnl(b) : colDef?.nested ? b[colDef.nested]?.[orderBy] : b[orderBy];
    if (orderBy === 'autoBid') {
      av = av ? av.shotsRemaining : -1;
      bv = bv ? bv.shotsRemaining : -1;
    }
    av = av ?? 0;
    bv = bv ?? 0;
    if (typeof av === 'string') return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return order === 'asc' ? av - bv : bv - av;
  });

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrder('desc');
    }
  };

  return (
    <>
    <Card sx={cardSx}>
      <MDBox sx={{ ...panelHeaderSx(t) }}>
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
          Player Breakdown ({players.length})
        </MDTypography>
        <IconButton size='small' onClick={() => setExpanded(!expanded)} sx={{ color: t.tableHeaderColor }}>
          <Icon>{expanded ? 'expand_less' : 'expand_more'}</Icon>
        </IconButton>
      </MDBox>
      <Collapse in={expanded}>
        {loading && !liveState ? (
          <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
            <Skeleton variant='rectangular' height={200} sx={{ borderRadius: '8px' }} />
          </MDBox>
        ) : sorted.length === 0 ? (
          <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={4} gap={1}>
            <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>No players</MDTypography>
          </MDBox>
        ) : (
          <TableContainer sx={scrollbarSx(t)}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow sx={tableHeadRowSx(t)}>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.key}>
                      {col.key !== 'autoBid' ? (
                        <TableSortLabel
                          active={orderBy === col.key}
                          direction={orderBy === col.key ? order : 'asc'}
                          onClick={() => handleSort(col.key)}
                          sx={{ '& .MuiTableSortLabel-icon': { color: `${t.tableHeaderColor} !important` } }}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((p, index) => {
                  const isTop = index === 0;
                  return (
                    <TableRow key={p.userId} sx={tableRowSx(t, { index, isTop })}>
                      <TableCell>
                        <MDBox display='flex' alignItems='center' gap={0.5}>
                          <MDTypography variant='caption' fontWeight={isTop ? 'bold' : 'medium'} sx={{ fontSize: '0.78rem', color: t.primaryText }}>{p.displayName}</MDTypography>
                          <Tooltip title='Copy user ID' placement='top'>
                            <IconButton size='small' onClick={() => handleCopyUid(p.userId)} sx={{ p: '2px', color: t.secondaryText, '&:hover': { color: '#e2001b' } }}>
                              <Icon sx={{ fontSize: '0.82rem !important' }}>content_copy</Icon>
                            </IconButton>
                          </Tooltip>
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{p.normalBidCount}</MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(p.normalBidTotal)}</MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(p.totalEarningsUsd)}</MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText }}>${fmt(p.earningsBreakdown?.blockRewardUsd)}</MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText }}>${fmt(p.earningsBreakdown?.bossUsd)}</MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText }}>${fmt(p.earningsBreakdown?.airdropUsd)}</MDTypography>
                      </TableCell>
                      <TableCell>
                        {(() => { const pnl = computePnl(p); return (
                          <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: pnl >= 0 ? '#4caf50' : '#f44336' }}>
                            {pnl >= 0 ? '' : '-'}${fmt(Math.abs(pnl))}
                          </MDTypography>
                        ); })()}
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.secondaryText }}>${fmt(p.airdropBidAmountUsd)}</MDTypography>
                      </TableCell>
                      <TableCell>
                        {p.autoBid ? (
                          <Chip label={`${p.autoBid.shotsRemaining}/${p.autoBid.shotsTotal}`} color='info' size='small' sx={{ height: 18, fontSize: '0.65rem' }} />
                        ) : (
                          <MDTypography variant='caption' sx={{ color: t.secondaryText }}>—</MDTypography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Card>
    <Snackbar open={!!copySnack} autoHideDuration={1500} onClose={() => setCopySnack('')} message={copySnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default PlayerBreakdown;
